import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button } from "../../components/admin/UI";
import { Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "../../lib/download";

const STAT_KEYS = [
    { k: "points", l: "PTS" },
    { k: "rebounds", l: "REB" },
    { k: "assists", l: "PD" },
    { k: "steals", l: "INT" },
    { k: "blocks", l: "CT" },
    { k: "fouls", l: "F" },
    { k: "minutes", l: "MIN" },
];

export default function MatchStats() {
    const { matchId } = useParams();
    const { activeClubId, canManageSports } = useAuth();
    const [match, setMatch] = useState(null);
    const [players, setPlayers] = useState([]);
    const [stats, setStats] = useState({}); // player_id -> {points, ...}
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!activeClubId || !matchId) return;
        api.get(`/matches/${matchId}`, { params: { club_id: activeClubId } }).then((r) => {
            setMatch(r.data);
            return api.get("/players", { params: { club_id: activeClubId, team_id: r.data.team_id, season_id: r.data.season_id } });
        }).then((r) => setPlayers(r.data));
        api.get(`/stats/match/${matchId}`, { params: { club_id: activeClubId } }).then((r) => {
            const m = {};
            r.data.forEach((s) => { m[s.player_id] = s; });
            setStats(m);
        });
    }, [activeClubId, matchId]);

    const setVal = (pid, key, v) => {
        setStats((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [key]: v === "" ? "" : Number(v) } }));
    };

    const submit = async () => {
        const entries = players.map((p) => ({
            player_id: p.id,
            points: Number(stats[p.id]?.points || 0),
            rebounds: Number(stats[p.id]?.rebounds || 0),
            assists: Number(stats[p.id]?.assists || 0),
            steals: Number(stats[p.id]?.steals || 0),
            blocks: Number(stats[p.id]?.blocks || 0),
            fouls: Number(stats[p.id]?.fouls || 0),
            minutes: stats[p.id]?.minutes ? Number(stats[p.id].minutes) : null,
        }));
        try {
            await api.post("/stats/match/bulk", { match_id: matchId, entries }, { params: { club_id: activeClubId } });
            toast.success("Statistiques sauvegardées");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const exportStats = async () => {
        setExporting(true);
        try {
            await downloadXlsx("/exports/stats", {
                club_id: activeClubId, season_id: match.season_id, team_id: match.team_id, match_id: matchId,
            }, "statistiques-match.xlsx");
            toast.success("Export Excel téléchargé");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setExporting(false);
        }
    };

    if (!match) return <div className="text-sm text-slate-500">Chargement…</div>;

    return (
        <div data-testid="match-stats-page">
            <Link to="/admin/matches" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-3"><ArrowLeft className="w-4 h-4 mr-1" /> Retour aux matchs</Link>
            <PageHeader
                title={`Stats : ASSMBB vs ${match.opponent}`}
                subtitle={`${match.date} · ${match.competition || "Match amical"}`}
                action={<div className="flex gap-2">
                    {canManageSports && <Button variant="secondary" onClick={exportStats} disabled={exporting}>{exporting ? "Export…" : "Exporter Excel"}</Button>}
                    <Button onClick={submit} data-testid="save-stats-btn"><Save className="w-4 h-4" /> Enregistrer</Button>
                </div>}
            />

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Joueur</th>
                                {STAT_KEYS.map((s) => (
                                    <th key={s.k} className="px-2 py-3 text-xs uppercase tracking-wider text-slate-500 text-center">{s.l}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {players.map((p) => (
                                <tr key={p.id} className="border-t border-slate-100" data-testid={`stat-row-${p.id}`}>
                                    <td className="px-4 py-2 font-semibold text-slate-900">{p.last_name?.toUpperCase()} {p.first_name}</td>
                                    {STAT_KEYS.map((s) => (
                                        <td key={s.k} className="px-2 py-2 text-center">
                                            <input
                                                type="number"
                                                min="0"
                                                value={stats[p.id]?.[s.k] ?? ""}
                                                onChange={(e) => setVal(p.id, s.k, e.target.value)}
                                                className="w-14 rounded-md border border-slate-200 px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                data-testid={`stat-${p.id}-${s.k}`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {players.length === 0 && <CardBody><div className="text-sm text-slate-500 text-center">Aucun joueur dans cette équipe.</div></CardBody>}
            </Card>
        </div>
    );
}
